import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type {
  ConfigCalendario,
  ConfigCores,
  ConfigEscrita,
  ConfigPapel,
  ConfigPlanner,
  ConfigTipografia,
  DadosPessoais,
  DocumentoMiolo,
  PaginaExtra,
  Paleta,
} from '@miolos/core';
import { construirDocumento, construirPaleta } from '@miolos/core';
import { useRecursos } from '@/recursos/EstadoRecursos';
import { passosJaDefinidos } from './passosDefinidos';
import { passosVisiveis, type DefinicaoPasso } from './Passos';

interface ContextoEditor {
  config: ConfigPlanner;
  paleta: Paleta;
  documento: DocumentoMiolo;
  /** Passos aplicáveis à configuração atual, na ordem do assistente. */
  passos: DefinicaoPasso[];
  /**
   * Etapas que já estão resolvidas: o que veio pronto do modelo ou do miolo
   * salvo, mais o que o usuário já percorreu nesta edição.
   */
  passosDefinidos: string[];
  /** Modelo escolhido na área de modelos prontos (vem da URL), se houver. */
  modeloInicial: string | null;
  /** O fluxo de criação ainda está sendo lido (evita piscar a lista). */
  carregandoFluxo: boolean;
  passo: number;
  irParaPasso: (indice: number) => void;
  avancar: () => void;
  voltar: () => void;
  atualizar: (patch: Partial<ConfigPlanner>) => void;
  /** Orientação da folha e margem de encadernação. */
  definirPapel: (patch: Partial<ConfigPapel>) => void;
  definirCores: (patch: Partial<ConfigCores>) => void;
  definirEscrita: (patch: Partial<ConfigEscrita>) => void;
  definirTipografia: (patch: Partial<ConfigTipografia>) => void;
  definirCalendario: (patch: Partial<ConfigCalendario>) => void;
  definirDados: (patch: Partial<DadosPessoais>) => void;
  alternarExtra: (extra: PaginaExtra) => void;
  definirConfig: (config: ConfigPlanner) => void;
  totalPassos: number;
}

const Contexto = createContext<ContextoEditor | null>(null);

export function ProvedorEditor({
  configInicial,
  modeloInicial = null,
  children,
}: {
  configInicial: ConfigPlanner;
  /** Id do modelo já aplicado ao abrir (área de modelos prontos). */
  modeloInicial?: string | null;
  children: ReactNode;
}) {
  const [config, setConfig] = useState<ConfigPlanner>(configInicial);
  const [passo, setPasso] = useState(0);
  /**
   * Começa já com as etapas que a configuração de entrada resolve: abrir um
   * modelo pronto ou um miolo salvo mostra, de saída, o que está decidido e o
   * que ainda falta.
   */
  const [definidos, setDefinidos] = useState<string[]>(() => {
    const iniciais = passosJaDefinidos(configInicial);
    // Um modelo aplicado já responde à primeira etapa (“de onde partir”).
    return modeloInicial ? ['modelo', ...iniciais] : iniciais;
  });
  const { liberado, carregando: carregandoFluxo } = useRecursos();

  const paleta = useMemo(() => construirPaleta(config.cores), [config.cores]);
  const documento = useMemo(() => construirDocumento(config), [config]);

  /**
   * A lista de passos depende da configuração e do que o administrador liberou:
   * sem páginas de dia, “Dias da semana” e “Quadros do dia” saem do assistente;
   * itens escondidos no fluxo de criação também não aparecem.
   */
  const passos = useMemo(() => passosVisiveis(config, liberado), [config, liberado]);
  const totalPassos = passos.length;

  /** Se a configuração encurtar a lista, o passo atual não pode ficar órfão. */
  const passoAtual = Math.max(0, Math.min(passo, totalPassos - 1));

  const valor = useMemo<ContextoEditor>(() => {
    const atualizar = (patch: Partial<ConfigPlanner>) => setConfig((atual) => ({ ...atual, ...patch }));

    return {
      config,
      paleta,
      documento,
      passos,
      passosDefinidos: definidos,
      modeloInicial,
      carregandoFluxo,
      passo: passoAtual,
      totalPassos,
      irParaPasso: (indice) => setPasso(Math.max(0, Math.min(totalPassos - 1, indice))),
      avancar: () => {
        // Avançar é dizer “esta etapa está resolvida”: ela passa a aparecer
        // marcada na lista, como as que vieram prontas do modelo.
        const atual = passos[passoAtual];
        if (atual) {
          setDefinidos((lista) => (lista.includes(atual.id) ? lista : [...lista, atual.id]));
        }
        setPasso(Math.min(totalPassos - 1, passoAtual + 1));
      },
      voltar: () => setPasso(Math.max(0, passoAtual - 1)),
      atualizar,
      definirCores: (patch) =>        setConfig((atual) => ({
          ...atual,
          temaId: atual.temaId === 'PERSONALIZADO' ? atual.temaId : 'PERSONALIZADO',
          cores: { ...atual.cores, ...patch },
        })),
      definirEscrita: (patch) => setConfig((atual) => ({ ...atual, escrita: { ...atual.escrita, ...patch } })),
      definirPapel: (patch) => setConfig((atual) => ({ ...atual, papel: { ...atual.papel, ...patch } })),
      definirTipografia: (patch) =>
        setConfig((atual) => ({ ...atual, tipografia: { ...atual.tipografia, ...patch } })),
      definirCalendario: (patch) =>
        setConfig((atual) => ({ ...atual, calendario: { ...atual.calendario, ...patch } })),
      definirDados: (patch) =>
        setConfig((atual) => ({ ...atual, dadosPessoais: { ...atual.dadosPessoais, ...patch } })),
      alternarExtra: (extra) =>
        setConfig((atual) => {
          const existe = atual.paginasExtras.includes(extra);
          return {
            ...atual,
            paginasExtras: existe
              ? atual.paginasExtras.filter((item) => item !== extra)
              : [...atual.paginasExtras, extra],
          };
        }),
      definirConfig: setConfig,
    };
  }, [config, paleta, documento, passoAtual, passos, totalPassos, carregandoFluxo, modeloInicial, definidos]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useEditor(): ContextoEditor {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useEditor precisa estar dentro de <ProvedorEditor>.');
  return contexto;
}
